import { Suspense, useState } from 'react';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { noop, useSuspenseQuery } from '@tanstack/react-query';
import { useFormatter } from 'use-intl';
import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { FolderLibraryIcon, SquareLock01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useAuth } from '@/features/auth';
import {
  workspaceTermsQuery,
  workspaceTermWorkspacesQuery,
} from '@/features/workspace-terms/queries';
import type { WorkspaceTerm } from '@/features/workspace-terms/types';
import { TERM_DATE_FORMAT } from '@/features/workspace-terms/constants';
import {
  getCurrentTerm,
  getRemainingWorkspaces,
  getTermNumber,
  getTermStatus,
  getTermUsage,
} from '@/features/workspace-terms/utils';
import { WorkspaceTypeIcon } from '@/features/workspaces/components/workspace-type-icon';
import { Badge } from '@/ui/components/badge';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Card } from '@/ui/components/card';
import { CreatedBy } from '@/ui/components/created-by';
import { Empty, EmptyContent, EmptyDescription, EmptyTitle } from '@/ui/components/empty';
import { Frame } from '@/ui/components/frame';
import { Header, HeaderCopyButton, HeaderOrganizationMenu } from '@/ui/components/header';
import { H1, H2 } from '@/ui/components/heading';
import { Icon } from '@/ui/components/icon';
import { MainContainer, MainHeader, MainScrollArea } from '@/ui/components/main';
import { Progress, ProgressTrack } from '@/ui/components/progress';
import { Spinner } from '@/ui/components/spinner';
import {
  StatPanel,
  StatPanelLabel,
  StatPanelTooltip,
  StatPanelValue,
} from '@/ui/components/stat-panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/components/table';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';

const TERM_STATUS_BADGE = {
  current: 'tertiary-accent',
  upcoming: 'tertiary',
  fullyUsed: 'tertiary-warning',
  over: 'tertiary-destructive',
  ended: 'tertiary',
} as const;

const TERM_STATUS_LABEL = {
  current: { en: 'Current', nb: 'Gjeldende' },
  upcoming: { en: 'Upcoming', nb: 'Kommende' },
  fullyUsed: { en: 'Fully used', nb: 'Brukt opp' },
  ended: { en: 'Ended', nb: 'Avsluttet' },
};

export const Route = createFileRoute('/_authed/settings/workspaces')({
  component: RouteComponent,
  loader: async ({ context }) => {
    const { organizationId } = context.auth.user;
    const terms = await context.queryClient.query({
      ...workspaceTermsQuery(organizationId),
      staleTime: 'static',
    });
    const term = getCurrentTerm(terms) ?? terms[0];
    if (!term) throw notFound();
    context.queryClient.query(workspaceTermWorkspacesQuery(organizationId, term.id)).catch(noop);
  },
});

function RouteComponent() {
  const format = useFormatter();
  const auth = useAuth();
  const { data: terms } = useSuspenseQuery(workspaceTermsQuery(auth.user.organizationId));
  const currentTerm = getCurrentTerm(terms);
  const [selectedTermId, setSelectedTermId] = useState<string>();
  const selectedTerm = terms.find((term) => term.id === selectedTermId) ?? currentTerm ?? terms[0];

  const termLabel = (term: WorkspaceTerm) =>
    t({ en: `Term ${getTermNumber(terms, term)}`, nb: `Periode ${getTermNumber(terms, term)}` });
  const period = (term: WorkspaceTerm) =>
    format.dateTimeRange(new Date(term.startsOn), new Date(term.endsOn), TERM_DATE_FORMAT);
  const termEnd = currentTerm && format.dateTime(new Date(currentTerm.endsOn), TERM_DATE_FORMAT);

  if (!selectedTerm) return null;

  return (
    <>
      <Header>
        <HeaderOrganizationMenu />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={FolderLibraryIcon} />
            {t({ en: 'Workspaces', nb: 'Arbeidsområder' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader className="max-md:hidden">
          <H1>{t({ en: 'Workspaces', nb: 'Arbeidsområder' })}</H1>
        </MainHeader>
        <MainContainer className="space-y-8">
          {currentTerm ? (
            <Frame stackedPanels className="@3xl:grid-cols-[2fr_1fr_1.25fr]">
              <StatPanel>
                <StatPanelValue>
                  {currentTerm.createdWorkspaces}
                  <span className="ml-1.5 text-muted text-sm font-normal">
                    / {currentTerm.includedWorkspaces}
                  </span>
                </StatPanelValue>
                <StatPanelLabel>
                  {t({ en: 'Created this term', nb: 'Opprettet denne perioden' })}
                  <StatPanelTooltip>
                    {t({
                      en: 'Includes archived workspaces.',
                      nb: 'Inkluderer arkiverte arbeidsområder.',
                    })}
                  </StatPanelTooltip>
                </StatPanelLabel>
              </StatPanel>
              <StatPanel>
                <StatPanelValue>{getRemainingWorkspaces(currentTerm)}</StatPanelValue>
                <StatPanelLabel>
                  {t({ en: 'Left this term', nb: 'Igjen denne perioden' })}
                  <StatPanelTooltip>
                    {t({
                      en: `Ends ${termEnd}. Unused slots don't carry over to the next term.`,
                      nb: `Slutter ${termEnd}. Ubrukte plasser overføres ikke til neste periode.`,
                    })}
                  </StatPanelTooltip>
                </StatPanelLabel>
              </StatPanel>
              <StatPanel>
                <StatPanelValue>{termLabel(currentTerm)}</StatPanelValue>
                <StatPanelLabel>
                  {t({ en: 'Current term', nb: 'Gjeldende periode' })}
                  <StatPanelTooltip>{period(currentTerm)}</StatPanelTooltip>
                </StatPanelLabel>
              </StatPanel>
            </Frame>
          ) : null}
          <section>
            <H2 className="mb-3">
              {t({ en: 'Workspaces per term', nb: 'Arbeidsområder per periode' })}
            </H2>
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t({ en: 'Term', nb: 'Periode' })}</TableHead>
                    <TableHead>{t({ en: 'Period', nb: 'Tidsrom' })}</TableHead>
                    <TableHead>{t({ en: 'Agreed', nb: 'Avtalt' })}</TableHead>
                    <TableHead>{t({ en: 'Created', nb: 'Opprettet' })}</TableHead>
                    <TableHead>{t({ en: 'Usage', nb: 'Bruk' })}</TableHead>
                    <TableHead>{t(common.status)}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {terms.map((term) => {
                    const status = getTermStatus(term);
                    return (
                      <TableRow key={term.id}>
                        <TableCell className="font-medium">{termLabel(term)}</TableCell>
                        <TableCell className="whitespace-nowrap">{period(term)}</TableCell>
                        <TableCell className="tabular-nums">{term.includedWorkspaces}</TableCell>
                        <TableCell
                          data-over={status === 'over' ? '' : undefined}
                          className="tabular-nums data-over:text-destructive"
                        >
                          {term.createdWorkspaces}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Progress
                              value={term.createdWorkspaces}
                              max={term.includedWorkspaces}
                              className="w-36"
                            >
                              <ProgressTrack
                                data-over={status === 'over' ? '' : undefined}
                                className="h-1.5 data-over:*:bg-red-9"
                              />
                            </Progress>
                            <span className="text-muted tabular-nums">
                              {format.number(getTermUsage(term), {
                                style: 'percent',
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge size="sm" variant={TERM_STATUS_BADGE[status]}>
                            {status === 'over'
                              ? t({
                                  en: `Over by ${term.createdWorkspaces - term.includedWorkspaces}`,
                                  nb: `${term.createdWorkspaces - term.includedWorkspaces} over`,
                                })
                              : t(TERM_STATUS_LABEL[status])}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </section>
        </MainContainer>
        <section>
          <Tabs value={selectedTerm.id} onValueChange={setSelectedTermId}>
            <TabsList className="px-x">
              {terms.map((term) => (
                <TabsTab key={term.id} value={term.id}>
                  {termLabel(term)}
                </TabsTab>
              ))}
            </TabsList>
            <TabsPanel value={selectedTerm.id} className="relative">
              <Suspense fallback={<Spinner kind="inset" />}>
                <TermWorkspaces termId={selectedTerm.id} />
              </Suspense>
            </TabsPanel>
          </Tabs>
        </section>
      </MainScrollArea>
    </>
  );
}

function TermWorkspaces({ termId }: { termId: string }) {
  const auth = useAuth();
  const { data: terms } = useSuspenseQuery(workspaceTermsQuery(auth.user.organizationId));
  const { data: workspaces } = useSuspenseQuery(
    workspaceTermWorkspacesQuery(auth.user.organizationId, termId),
  );
  const restricted =
    (terms.find((term) => term.id === termId)?.createdWorkspaces ?? 0) - workspaces.length;
  const restrictedLabel = t({
    en:
      restricted === 1
        ? "1 workspace is in projects you can't access"
        : `${restricted} workspaces are in projects you can't access`,
    nb:
      restricted === 1
        ? '1 arbeidsområde er i prosjekter du ikke har tilgang til'
        : `${restricted} arbeidsområder er i prosjekter du ikke har tilgang til`,
  });

  if (workspaces.length === 0)
    return (
      <Empty>
        <EmptyContent>
          <EmptyTitle>
            {t({ en: 'Workspaces this term', nb: 'Arbeidsområder denne perioden' })}
          </EmptyTitle>
          <EmptyDescription>
            {restricted > 0
              ? restrictedLabel
              : t({
                  en: 'No workspaces were created in this term.',
                  nb: 'Ingen arbeidsområder ble opprettet i denne perioden.',
                })}
          </EmptyDescription>
        </EmptyContent>
      </Empty>
    );

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t({ en: 'Workspace', nb: 'Arbeidsområde' })}</TableHead>
            <TableHead>{t({ en: 'Created', nb: 'Opprettet' })}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workspaces.map((workspace) => (
            <TableRow key={workspace.id}>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  {workspace.type ? (
                    <WorkspaceTypeIcon type={workspace.type} className="size-4 shrink-0" />
                  ) : null}
                  {workspace.isArchived ? (
                    <>
                      <span className="line-clamp-1">{workspace.name}</span>
                      <Badge size="xs" variant="tertiary">
                        {t({ en: 'Archived', nb: 'Arkivert' })}
                      </Badge>
                    </>
                  ) : (
                    <Link
                      to="/project/$projectId/workspaces/$workspaceId"
                      params={{ projectId: workspace.projectId, workspaceId: workspace.id }}
                      className="line-clamp-1 hover:underline"
                    >
                      {workspace.name}
                    </Link>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <CreatedBy user={workspace.createdBy} creationDate={workspace.createdAt} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {restricted > 0 ? (
        <p className="mt-3 flex items-center gap-1.5 text-muted text-xs">
          <Icon icon={SquareLock01Icon} className="size-3.5 shrink-0" />
          {restrictedLabel}
        </p>
      ) : null}
    </>
  );
}
